import { JSXElementConstructor, useCallback } from 'react';

import { ViableAccountsType } from '@givebacks/api/core';
import { Stack } from 'ui';

import { AvailableBalanceModal } from '../../../molecules/modals/AvailableBalanceModal/AvailableBalanceModal';
import { BankAccountModal } from '../../../molecules/modals/BankAccountModal/BankAccountModal';
import { CashbackDonationsFlowConfirmModal } from '../../../molecules/modals/CashbackDonationsFlowConfirmModal/CashbackDonationsFlowConfirmModal';
import { CashbackDonationsFlowDonationModal } from '../../../molecules/modals/CashbackDonationsFlowDonationModal/CashbackDonationsFlowDonationModal';
import { CreateHyperWalletAccountModal } from '../../../molecules/modals/CreateHypeWalletAccountModal/CreateHyperWalletAccountModal';
import { LinkedAccountsModal } from '../../../molecules/modals/LinkedAccountsModal/LinkedAccountsModal';
import { PayPalAccountModal } from '../../../molecules/modals/PayPalAccountModal/PayPalAccountModal';
import { SelectCauseModal } from '../../../molecules/modals/SelectCauseModal/SelectCauseModal';
import { TransferAvailableBalanceModal } from '../../../molecules/modals/TransferAvailableBalanceModal/TransferAvailableBalanceModal';
import { VenmoAccountModal } from '../../../molecules/modals/VenmoAccountModal/VenmoAccountModal';
import { RemoveAccountModal } from './RemoveAccountModal';
import {
  TransferAccountModalIds,
  TransferAccountModalTitleWithBackButton,
  TransferAccountsModalGroupProvider,
  useTransferAccountsModalGroupProvider,
} from './TransferAccountModalGroup.context';

type ButtonChildProps = ReturnType<typeof useTransferAccountsModalGroupProvider>;

type TransferAccountsModalGroupProps = {
  openButton: (arg0: ButtonChildProps) => React.ReactNode;

  // Pass the donation modal flow through
  MakeADonationComponent?: JSXElementConstructor<any>;

  children?: (arg0: ButtonChildProps) => React.ReactNode;
};

interface TransferAccountButtonWrapperProps {
  children: (arg0: ButtonChildProps) => React.ReactNode;
}

type AccountModalMap = Extract<
  TransferAccountModalIds,
  'CreateHyperwalletAccount' | 'ConnectBank' | 'ConnectVenmo' | 'ConnectPayPal'
>;

type AccountNames = ViableAccountsType | 'hyperWallet';

const ACCOUNTS_MODAL_MAP: Record<AccountNames, AccountModalMap> = {
  bank: 'ConnectBank',
  hyperWallet: 'CreateHyperwalletAccount',
  paypal: 'ConnectPayPal',
  venmo: 'ConnectVenmo',
} as const;

const PayPalAccountModalWrapper = () => {
  const { opened, close, props } = useTransferAccountsModalGroupProvider({
    id: 'ConnectPayPal',
  });

  return (
    <PayPalAccountModal
      opened={opened}
      onClose={close}
      // title={
      //   <TransferAccountModalTitleWithBackButton>
      //     Connect My PayPal Account
      //   </TransferAccountModalTitleWithBackButton>
      // }
      modalProps={props}
    />
  );
};

const VenmoAccountModalWrapper = () => {
  const { opened, close, props } = useTransferAccountsModalGroupProvider({
    id: 'ConnectVenmo',
  });

  return (
    <VenmoAccountModal
      opened={opened}
      onClose={close}
      // title={
      //   <TransferAccountModalTitleWithBackButton>
      //     Connect My Venmo
      //   </TransferAccountModalTitleWithBackButton>
      // }
      modalProps={props}
    />
  );
};

const BankAccountModalWrapper = () => {
  const { opened, close, props } = useTransferAccountsModalGroupProvider({
    id: 'ConnectBank',
  });

  return (
    <BankAccountModal
      opened={opened}
      onClose={close}
      // title={
      //   <TransferAccountModalTitleWithBackButton>
      //     Connect My Bank
      //   </TransferAccountModalTitleWithBackButton>
      // }
      modalProps={props}
    />
  );
};

const CreateHyperWalletAccountModalWrapper = () => {
  const { opened, close, openModalById, props, setMeta, meta } =
    useTransferAccountsModalGroupProvider({
      id: 'CreateHyperwalletAccount',
    });

  const handleAccountCreation = useCallback(() => {
    const nextModal = meta.createAccountType;

    if (nextModal) {
      openModalById(ACCOUNTS_MODAL_MAP[nextModal]);
    }

    setMeta((prev) => ({ ...prev, createAccountType: undefined }));
  }, [meta.createAccountType, openModalById, setMeta]);

  return (
    <CreateHyperWalletAccountModal
      opened={opened}
      onClose={close}
      title={
        <TransferAccountModalTitleWithBackButton>
          Connect Account
        </TransferAccountModalTitleWithBackButton>
      }
      modalProps={props}
      onCreateAccount={handleAccountCreation}
    />
  );
};

const RemoveAccountWrapper = () => {
  const { opened, close, openBack, meta, props, setMeta } = useTransferAccountsModalGroupProvider({
    id: 'RemoveAccount',
  });

  const handleCancel = useCallback(() => {
    setMeta((prev) => ({ ...prev, removeAccountType: undefined, removeAccountId: undefined }));
    openBack();
  }, [openBack, setMeta]);

  return (
    <RemoveAccountModal
      modalProps={props}
      onCancel={handleCancel}
      accountType={meta?.removeAccountType}
      accountId={meta?.removeAccountId}
      opened={opened}
      onClose={close}
      {...props}
      title={
        <TransferAccountModalTitleWithBackButton>
          Remove Account
        </TransferAccountModalTitleWithBackButton>
      }
      onRemove={handleCancel}
    />
  );
};

const LinkedAccountsModalWrapper = () => {
  const { opened, close, openModalById, props, setMeta } = useTransferAccountsModalGroupProvider({
    id: 'LinkedAccounts',
  });

  const handleOpenAccount = useCallback(
    (account: AccountNames) => {
      openModalById(ACCOUNTS_MODAL_MAP[account]);
    },
    [openModalById]
  );

  const handleSetPendingAccount = useCallback(
    (account: ViableAccountsType) => {
      setMeta((prev) => ({ ...prev, createAccountType: account }));
    },
    [setMeta]
  );

  const onRemove = useCallback(
    (account: ViableAccountsType, accountId: string) => {
      setMeta((prev) => ({ ...prev, removeAccountType: account, removeAccountId: accountId }));
      openModalById('RemoveAccount');
    },
    [openModalById, setMeta]
  );
  return (
    <LinkedAccountsModal
      opened={opened}
      onClose={close}
      modalProps={props}
      onRemove={onRemove}
      onConnectAccount={handleOpenAccount}
      onPendingAccount={handleSetPendingAccount}
    />
  );
};

const TransferAvailableBalanceWrapper = () => {
  const { opened, close, openModalById, props, setMeta } = useTransferAccountsModalGroupProvider({
    id: 'TransferAvailableBalance',
  });

  const handleOpenAccount = useCallback(
    (account: AccountNames) => {
      openModalById(ACCOUNTS_MODAL_MAP[account]);
    },
    [openModalById]
  );

  const handleSetPendingAccount = useCallback(
    (account: ViableAccountsType) => {
      setMeta((prev) => ({ ...prev, createAccountType: account }));
    },
    [setMeta]
  );

  return (
    <TransferAvailableBalanceModal
      opened={opened}
      onClose={close}
      modalProps={props}
      title={
        <TransferAccountModalTitleWithBackButton>
          Transfer Available Balance
        </TransferAccountModalTitleWithBackButton>
      }
      onCreateAccount={handleOpenAccount}
      onPendingAccount={handleSetPendingAccount}
      onTransferSuccess={close}
    />
  );
};

const AvailableBalanceModalWrapper = () => {
  const { opened, close, openModalById, props } = useTransferAccountsModalGroupProvider({
    id: 'AvailableBalance',
  });

  return (
    <AvailableBalanceModal
      keepMounted
      opened={opened}
      onClose={close}
      onTransferClick={() => openModalById('TransferAvailableBalance')}
      onDonationClick={() => {
        openModalById('SelectCause');
      }}
      modalProps={props}
    />
  );
};

const TransferAccountButtonWrapper = ({ children }: TransferAccountButtonWrapperProps) => {
  const props = useTransferAccountsModalGroupProvider({});
  return children(props);
};

const TransferAccountsModalGroup = ({ openButton }: TransferAccountsModalGroupProps) => {
  return (
    <TransferAccountsModalGroupProvider meta={{}}>
      <Stack>
        <TransferAccountButtonWrapper>{openButton}</TransferAccountButtonWrapper>
      </Stack>

      <AvailableBalanceModalWrapper />
      <TransferAvailableBalanceWrapper />
      <LinkedAccountsModalWrapper />
      <RemoveAccountWrapper />
      <CreateHyperWalletAccountModalWrapper />
      <BankAccountModalWrapper />
      <VenmoAccountModalWrapper />
      <PayPalAccountModalWrapper />

      <SelectCauseModal />
      <CashbackDonationsFlowDonationModal />
      <CashbackDonationsFlowConfirmModal />
    </TransferAccountsModalGroupProvider>
  );
};

export { TransferAccountsModalGroup };
